import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper'
import { ScrollingModule } from '@angular/cdk/scrolling'
import { LocationStrategy, PathLocationStrategy } from '@angular/common'
import * as Sentry from '@sentry/angular-ivy'
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport
} from '@angular/common/http'
import {
  APP_INITIALIZER,
  type ApplicationConfig,
  importProvidersFrom
} from '@angular/core'
import { MAT_DATE_LOCALE } from '@angular/material/core'
import { provideAnimations } from '@angular/platform-browser/animations'
import { Router } from '@angular/router'

// libraries
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule
} from '@ngx-translate/core'
import { StarRatingModule } from 'angular-star-rating'
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer'
import { NgxGlideModule } from 'ngx-glide'
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import {
  KeycloakAngularModule,
  type KeycloakOptions,
  KeycloakService
} from 'keycloak-angular'
import Keycloak from 'keycloak-js'
import { LiveChatWidgetModule } from '@livechat/widget-angular'
import { GoogleTagManagerModule } from 'angular-google-tag-manager'
import { provideNgxMask } from 'ngx-mask'
import { GoogleMapsModule } from '@angular/google-maps'
import { ServiceWorkerModule } from '@angular/service-worker'

// ef libraries
import {
  B2CModule,
  type Environment as B2cEnvironment
} from '@inside-hub-app/customer-portal-b2c-client'
import {
  PpMissingTranslationHandler,
  PpModule,
  type PpModuleConfig,
  PpTranslationLoader,
  TranslationService
} from '@inside-hub-app/ef-ng-pp-client'
import { EfRemoteConfigModule } from '@inside-hub-app/ef-remote-config'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import {
  CptJsonpInterceptor,
  CustomerPortalSharedModule,
  IconModule,
  LoginService,
  ChatBotService
} from '@inside-hub-app/customer-portal-shared'
import {
  type CustomerPortalConfig,
  CustomerPortalCustomerPortalConfigModule
} from '@inside-hub-app/customer-portal-config'

import { AppRoutingModule } from './app-routing.module'

type WindowWithRemoteConfig = Window & {
  ef: {
    remoteConfig: {
      configuration: CustomerPortalConfig
    }
  }
}

const remoteConfig = (window as unknown as WindowWithRemoteConfig).ef.remoteConfig
  .configuration

const ppEnvironments: PpModuleConfig = {
  storagePrefix: remoteConfig['ppEnvironments.cpt.storagePrefix'],
  translationPrefix: remoteConfig['ppEnvironments.cpt.translationPrefix'],
  environment: {
    domain: remoteConfig['ppEnvironments.cpt.environment.domain'],
    baseUrl: remoteConfig['ppEnvironments.cpt.environment.baseUrl'],
    authorizationExcludedUrls: [
      {
        pattern:
          remoteConfig[
            'ppEnvironments.cpt.environment.authorizationExcludedUrls.0.pattern'
          ]
      },
      {
        pattern:
          remoteConfig[
            'ppEnvironments.cpt.environment.authorizationExcludedUrls.1.pattern'
          ]
      },
      {
        pattern:
          remoteConfig[
            'ppEnvironments.cpt.environment.authorizationExcludedUrls.2.pattern'
          ]
      }
    ]
  }
}

export const keycloakService = new KeycloakService()

export const keycloakConfig: KeycloakOptions = {
  config: {
    clientId: remoteConfig['keycloak.config.clientId'],
    realm: remoteConfig['keycloak.config.realm'],
    url: remoteConfig['keycloak.config.url']
  },
  bearerExcludedUrls: remoteConfig['keycloak.bearerExcludedUrls'],
  enableBearerInterceptor: remoteConfig['keycloak.enableBearerInterceptor'],
  initOptions: {
    checkLoginIframe: remoteConfig['keycloak.initOptions.checkLoginIframe'],
    checkLoginIframeInterval:
      remoteConfig['keycloak.initOptions.checkLoginIframeInterval'],
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri:
      window.location.origin +
      remoteConfig['keycloak.initOptions.silentCheckSsoRedirectUri'],
    pkceMethod: 'S256'
  },
  loadUserProfileAtStartUp: remoteConfig['keycloak.loadUserProfileAtStartUp']
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      AppRoutingModule,
      KeycloakAngularModule,
      NgxSkeletonLoaderModule,
      NgxExtendedPdfViewerModule,
      NgxGlideModule,
      GoogleMapsModule,
      ScrollingModule,
      LoggerModule.forRoot({
        enableSourceMaps: !remoteConfig.production,
        level: remoteConfig.production
          ? NgxLoggerLevel.OFF
          : NgxLoggerLevel.DEBUG
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [TranslationService, HttpClient]
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: PpMissingTranslationHandler
        }
      }),
      B2CModule.forRoot({
        environment: remoteConfig['b2c.environment'] as B2cEnvironment,
        apiKey: remoteConfig['b2c.apiKey']
      }),
      PpModule.forRoot(ppEnvironments),
      HubChildTranslateModule,
      GoogleTagManagerModule.forRoot({
        id: remoteConfig['googleTagManager.gtmId']
      }),
      LiveChatWidgetModule,
      StarRatingModule.forRoot(),
      IconModule.forRoot({
        iconsPath: './assets/icons',
        useKcResourcePath: false
      }),
      CustomerPortalSharedModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: remoteConfig.pwaEnabled,
        registrationStrategy: 'registerWhenStable:30000'
      }),
      EfRemoteConfigModule,
      CustomerPortalCustomerPortalConfigModule
    ),
    provideAnimations(),
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: KeycloakService, useValue: keycloakService },
    // HubAuthStore (PpModule's XAuthTokenInterceptor) injects the raw Keycloak instance
    { provide: Keycloak, useFactory: () => keycloakService.getKeycloakInstance() },
    { provide: MAT_DATE_LOCALE, useValue: 'de-CH' },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    },
    { provide: Window, useValue: window },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        // no-op initializer to eagerly instantiate Sentry.TraceService
      },
      deps: [Sentry.TraceService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CptJsonpInterceptor,
      multi: true
    },
    LoginService,
    ChatBotService,
    provideNgxMask(),
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport())
  ]
}

export function HttpLoaderFactory (
  translationService: TranslationService,
  _http: HttpClient
): PpTranslationLoader {
  if (window != null) {
    const params = new URL(window.location.href).searchParams
    if (params != null) {
      const showTranslationKeys = params.get('showTranslationKeys')
      if (showTranslationKeys === 'true') {
        return new PpTranslationLoader(
          [{ type: '', prefix: '' }],
          '',
          translationService
        )
      }
    }
  }

  return new PpTranslationLoader(
    [
      {
        type: 'customerPortal',
        prefix: 'customer-portal.'
      },
      {
        type: 'customerPortal',
        prefix: 'kc.'
      },
      {
        type: 'vp-modules',
        prefix: 'vp-modules.'
      }
    ],
    '',
    translationService,
    ['it', 'fr', 'de', 'en']
  )
}



